import { resolveCustomQuestions } from "@scoach/types";
import { useEffect, useRef } from "react";

import { effectiveGeminiKey } from "../../../../lib/geminiKey.ts";
import { useAuthStore } from "../../../auth/store.ts";
import { generateCustomSummary } from "../gemini/customSummary.ts";
import { useLiveMeetingStore } from "../store.ts";

/** How often to re-answer the rep's custom questions from the growing transcript. */
const REFRESH_MS = 45_000;
/** Small delay before the first pass so a few lines of transcript exist. */
const FIRST_PASS_MS = 8_000;

/**
 * Live-answers the rep's per-user custom questions from the meeting transcript,
 * on the user's own Gemini key, and pushes the results into the live store so the
 * "My questions" tab can show which have been answered so far (X / Y).
 *
 * Transcript-only (no web grounding) — see generateCustomSummary. Runs only while
 * there is a key + questions, and only re-runs when new speech has arrived since
 * the last pass, so idle meetings don't burn tokens.
 */
export function useLiveCustomSummary(customQuestionKey?: string): void {
  const settings = useAuthStore((s) => s.user?.settings);
  // Use the per-meeting question set (role / meeting type) when one was chosen, else
  // the user's edited set for that key, else their global list, else the defaults.
  const questions = resolveCustomQuestions(customQuestionKey, settings);
  const enabled = settings?.customSummaryEnabled !== false && questions.length > 0;

  const runningRef = useRef(false);
  const lastLenRef = useRef(0);

  useEffect(() => {
    if (!enabled || !effectiveGeminiKey()) return;
    let cancelled = false;

    async function run() {
      if (runningRef.current) return;
      const { transcript } = useLiveMeetingStore.getState();
      // Only regenerate when there's new speech since the last pass.
      if (transcript.length === 0 || transcript.length === lastLenRef.current) return;
      runningRef.current = true;
      lastLenRef.current = transcript.length;
      try {
        const answers = await generateCustomSummary(transcript, questions);
        if (!cancelled && answers.length > 0) {
          useLiveMeetingStore.getState().setCustomSummary(answers);
        }
      } catch {
        // Transient (rate limit / network) — the next tick will retry.
        lastLenRef.current = 0;
      } finally {
        runningRef.current = false;
      }
    }

    const first = setTimeout(run, FIRST_PASS_MS);
    const timer = setInterval(run, REFRESH_MS);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(timer);
    };
  }, [enabled, questions]);
}
